import { useEffect } from "react";
import Intercom, { update as updateIntercom } from "@intercom/messenger-js-sdk";
import { useAuth } from "../lib/auth";

const INTERCOM_APP_ID = "hf1th8qf";

// MCP hand-off links (/connect, /config-review, /test-case-review) render as a
// bare single-purpose popup — no support widget. Base-path safe: matches the
// segment anywhere so /gcp/connect/... is covered too.
const LINK_ROUTE_RE = /\/(connect|config-review|test-case-review)\//;

// GL1: the launcher is a fixed, very-high-z-index bottom-right bubble that
// lands on top of any panel whose action buttons live in a bottom-right
// footer (e.g. Create/Cancel on the trigger panels) — those buttons render
// but are unclickable underneath it. Fighting z-index per panel doesn't
// scale, so instead hide the launcher globally whenever such a footer is
// in the DOM, using Intercom's own `hide_default_launcher` toggle.
//
// Every footer class in the app is listed here, not just the slide-over one:
// the launcher outranks a centred modal too, so `.modal-footer` /
// `.ps-modal-footer` dialogs were still losing their Cancel button. Any new
// bottom-aligned action row must be added here.
export const FOOTER_SELECTOR = [
  ".admin-slideover-footer",
  ".modal-footer",
  ".ps-modal-footer",
  // Bottom-pinned action in the onboarding sidebar ("Explore dashboard on
  // your own") — the launcher sat on its right edge for the whole setup flow.
  ".ob-skip",
].join(",");

export function IntercomWidget(): null {
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.email) return;
    if (LINK_ROUTE_RE.test(window.location.pathname)) return;

    Intercom({
      app_id: INTERCOM_APP_ID,
      user_id: user.sub,
      name: user.name,
      email: user.email,
    });
  }, [user?.email, user?.sub, user?.name]);

  useEffect(() => {
    if (!user?.email) return;

    const syncLauncherVisibility = () => {
      const footerOpen = document.querySelector(FOOTER_SELECTOR) !== null;
      updateIntercom({ hide_default_launcher: footerOpen });
    };

    syncLauncherVisibility();
    const observer = new MutationObserver(syncLauncherVisibility);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [user?.email]);

  return null;
}
